<script setup lang="ts">
import * as Sentry from '@sentry/nuxt'

const documentLink = useDocumentLink()
const combinedData = useState<{
  title: {
    subtitle: string
    title: string
    imageUrl: string
  }
  artwork: {
    imageUrl: string
  }
}>('combinedData', () => ({
  title: {
    subtitle: 'Срочно нужны деньги?',
    title: 'Займы со скоростью света',
    imageUrl: '',
  },
  artwork: {
    imageUrl: '',
  },
}))

const link = documentLink('/combined/random').replace('/get/', '')

await callOnce(async () => {
  try {
    const response = await $fetch<{
      title: { subtitle: string, title: string, imageUrl: string }
      artwork: { imageUrl: string }
    }>(link)

    combinedData.value = {
      title: response.title,
      artwork: response.artwork,
    }
  }
  catch (e) {
    console.error('Error getting combined data:', e)

    // Отправляем ошибку в Sentry с контекстом
    Sentry.withScope((scope) => {
      scope.setTag('component', 'PageTitle')
      scope.setTag('action', 'load_combined_data')
      scope.setContext('request_data', {
        link,
      })
      scope.setLevel('warning') // Это не критическая ошибка, есть fallback
      Sentry.captureException(e)
    })
  }
})

const currentText = computed(() => combinedData.value.title)
const artworkImage = computed(() => combinedData.value.artwork)

const titleImgUrl = computed(() => {
  if (currentText.value.imageUrl) {
    return documentLink(currentText.value.imageUrl.replace('/get/', ''))
  }
  return null
})

const artworkImgUrl = computed(() => {
  if (artworkImage.value.imageUrl) {
    return documentLink(artworkImage.value.imageUrl.replace('/get/', ''))
  }
  return '/img/paintings/ka.jpg' // дефолтное изображение
})

const img = useImage()
const backgroundUrl = computed(() => {
  return img(artworkImgUrl.value, {
    format: 'webp',
    densities: [1, 2],
    quality: 80,
    modifiers: {
      width: 1920,
      height: 1080,
      fit: 'cover',
      gravity: 'center',
    },
    preload: true,
  })
})

const titleIconUrl = computed(() => {
  if (!titleImgUrl.value)
    return null
  return img(titleImgUrl.value, {
    format: 'webp',
    width: 80,
    height: 80,
    quality: 70,
  })
})
</script>

<template>
  <div class="text-center grid min-h-20 container mx-auto px-6 text-white relative z-10">
    <div class="image-wrapper">
      <NuxtImg
        :src="backgroundUrl"
        :placeholder="[50, 25, 75, 5]"
        loading="lazy"
        decoding="async"
        class="background-image"
        preload
      />
      <div class="absolute inset-0 bg-gradient-to-b from-black/30 to-black/50 z-[-1]" />

      <h2 class="subtitle">
        {{ currentText.subtitle }}
      </h2>
      <h1 class="pagetitle title">
        {{ currentText.title }}
        <template v-if="titleIconUrl">
          <NuxtImg
            v-if="titleIconUrl"
            :src="titleIconUrl"
            :placeholder="[20, 10]"
            loading="lazy"
            width="40"
            height="40"
            class="inline"
            alt=""
            fetchpriority="high"
          />
        </template>
      </h1>
    </div>
  </div>
</template>

<style scoped>
@reference "assets/css/main.css";

.background-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
}
.image-wrapper {
  position: relative;
  isolation: isolate;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 4rem 1rem;
  border-radius: 1rem;
  overflow: hidden;
  background-color: rgba(0,146,224, 1);
  view-transition-name: hero-image;
  content-visibility: auto;
}

.subtitle {
  @apply text-lg text-white/90;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.pagetitle {
  @apply text-3xl md:text-4xl lg:text-5xl;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
  font-weight: 700;
  max-width: 80%;
  margin: 0 auto;
}

@media (max-width: 640px) {
  .container {
    padding: 1rem 1rem;
  }
  .image-wrapper {
    padding: 3.5rem 1rem;
  }
  .pagetitle {
    font-size: 1.75rem;
    max-width: 100%;
  }
}
</style>
